import { AnimationControls, motion, useAnimation } from "motion/react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/helpers/cn";
import { Icon } from "@/shared/ui";
import { NotificationQuestSuccessState } from "@/domains/notification/constants/notificationState";

type Props = {
  className?: string;
} & NotificationQuestSuccessState;

const rewardAnimation = async (control: AnimationControls, delay: number) => {
  await control.start({
    visibility: "hidden",
    opacity: 0,
    translateY: "300%",
    transition: { delay, type: "spring" },
  });
  await control.start({
    visibility: "visible",
    transition: { duration: 0.1, type: "spring" },
  });
  await control.start({
    opacity: 1,
    scale: 2,
    transition: { duration: 0.2, type: "spring" },
  });
  await control.start({
    translateY: "none",
    scale: 1,
    transition: { duration: 0.3, type: "spring" },
  });
};

export default function NotificationQuestSuccess({
  className,
  donat,
  exp,
  money,
  questName,
  thing,
}: Props) {
  const { t } = useTranslation("notification");
  const donatAnimationControls = useAnimation();
  const moneyAnimationControls = useAnimation();
  const thingAnimationControls = useAnimation();
  const expAnimationControls = useAnimation();

  const sequence = useCallback(async () => {
    rewardAnimation(donatAnimationControls, 0.1);
    rewardAnimation(moneyAnimationControls, 0.4);
    rewardAnimation(thingAnimationControls, 0.7);
    rewardAnimation(expAnimationControls, 1);
  }, []);

  useEffect(() => {
    sequence();
  }, []);

  return (
    <div className={cn("flex flex-col items-center z-1", className)}>
      <motion.div
        initial={{ opacity: 0, translateY: "-100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "-100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          delay: 0.1,
          duration: 0.3,
        }}
        className="flex items-center gap-2.5 mt-5.5"
      >
        <Icon
          category="notification"
          name="complete"
          className="w-[2rem] h-[1.75rem] bg-[#84FF97]"
        />
        <div className="text-20 font-bold text-[#84FF97] whitespace-nowrap">
          {t("notification.questCompleted")}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, translateY: "100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          duration: 0.3,
          delay: 0.4,
        }}
        className="mt-3"
      >
        <div className="text-18 text-white font-bold text-center">{questName}</div>
        <div className="text-15 text-[#84FF97] font-medium text-center">
          {t("notification.rewardsForPerfomance")}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, translateY: "100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          duration: 0.3,
          delay: 0.7,
        }}
        className="flex items-center gap-2.5 bg-[#84FF97]/10 px-2.5 py-2 rounded-[0.5rem] mt-3.5"
      >
        <motion.div
          animate={donatAnimationControls}
          className="flex items-center gap-1.5 invisible"
        >
          <Icon category="notification" name="donat" className="w-[1.25rem] bg-[#84FF97]" />
          <div className="text-15 text-white font-bold">{donat}</div>
        </motion.div>
        <div className="w-[1px] h-3 bg-white/50 mix-blend-overlay" />
        <motion.div
          animate={moneyAnimationControls}
          className="flex items-center gap-1.5 invisible"
        >
          <Icon
            category="notification"
            name="money"
            className="w-[1.5rem] h-[1.25rem] bg-[#84FF97]"
          />
          <div className="text-15 text-white font-bold">{money}</div>
        </motion.div>
        <div className="w-[1px] h-3 bg-white/50 mix-blend-overlay" />
        <motion.div
          animate={thingAnimationControls}
          className="flex items-center gap-1.5 invisible"
        >
          <Icon
            category="notification"
            name="thing"
            className="w-[1.125rem] h-[1.25rem] bg-[#84FF97]"
          />
          <div className="text-15 text-white font-bold">{thing}</div>
        </motion.div>
        <div className="w-[1px] h-3 bg-white/50 mix-blend-overlay" />
        <motion.div animate={expAnimationControls} className="flex items-center gap-1.5 invisible">
          <Icon
            category="notification"
            name="exp"
            className="w-[1.75rem] h-[1.25rem] bg-[#84FF97]"
          />
          <div className="text-15 text-white font-bold">{exp}</div>
        </motion.div>
      </motion.div>
    </div>
  );
}
